import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { Mic, MicOff, PhoneOff, Video, VideoOff } from 'lucide-react';
import type { InterviewSession } from '../api';

/**
 * P2: videointervju mellan marken och en sökande (Qmeet/LiveKit), byggd på InterviewMeeting.tsx
 * men utan livetranskribering – intervjun spelas inte in och inget sparas (GDPR, och Whisper kostar).
 * Kamera och mikrofon startar på.
 */
export function InterviewCall({ session, onClose }: { session: InterviewSession; onClose: () => void }) {
  const roomRef = useRef<Room | null>(null);
  const [connected, setConnected] = useState(false);
  const [failed, setFailed] = useState('');
  const [mic, setMic] = useState(false);
  const [cam, setCam] = useState(false);
  const [remoteName, setRemoteName] = useState<string | null>(null);
  const [remoteCam, setRemoteCam] = useState(false);
  const remoteVideo = useRef<HTMLVideoElement>(null);
  const selfVideoEl = useRef<HTMLVideoElement | null>(null);
  const audioEls = useRef<Map<string, HTMLMediaElement>>(new Map());

  const selfVideo = useCallback((el: HTMLVideoElement | null) => {
    selfVideoEl.current = el;
    const pub = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Camera);
    if (el && pub?.track) pub.track.attach(el);
  }, []);

  useEffect(() => {
    if (!session.livekitUrl) { setFailed('Videomötet saknar adress'); return; }
    const r = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = r;
    const sync = () => {
      const p = Array.from(r.remoteParticipants.values())[0];
      setRemoteName(p ? (p.name || p.identity) : null);
      const pub = p?.getTrackPublication(Track.Source.Camera);
      const on = !!(pub?.isSubscribed && pub.track && !pub.isMuted);
      setRemoteCam(on);
      if (on && remoteVideo.current) pub!.track!.attach(remoteVideo.current);
    };
    r.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
      if (track.kind === 'audio') {
        const el = track.attach();
        el.style.display = 'none';
        document.body.appendChild(el);
        audioEls.current.set(participant.identity, el);
      }
      sync();
    });
    r.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
      if (track.kind === 'audio') { audioEls.current.get(participant.identity)?.remove(); audioEls.current.delete(participant.identity); }
      sync();
    });
    for (const ev of [RoomEvent.TrackMuted, RoomEvent.TrackUnmuted, RoomEvent.ParticipantConnected, RoomEvent.ParticipantDisconnected]) r.on(ev, sync);
    r.on(RoomEvent.Connected, () => setConnected(true));
    r.on(RoomEvent.Disconnected, () => setConnected(false));
    r.connect(session.livekitUrl, session.livekitToken, { autoSubscribe: true })
      .then(async () => {
        try { await r.localParticipant.setMicrophoneEnabled(true); setMic(true); } catch { /* ingen mikrofon */ }
        try {
          const pub = await r.localParticipant.setCameraEnabled(true);
          setCam(true);
          if (pub?.track && selfVideoEl.current) pub.track.attach(selfVideoEl.current);
        } catch { /* ingen kamera */ }
        sync();
      })
      .catch(e => setFailed(e?.message || 'Kunde inte ansluta till videomötet'));
    return () => {
      audioEls.current.forEach(el => el.remove());
      audioEls.current.clear();
      r.removeAllListeners();
      r.disconnect();
      roomRef.current = null;
    };
  }, [session.livekitToken, session.livekitUrl]);

  const toggleMic = async () => {
    const on = !mic;
    try { await roomRef.current?.localParticipant.setMicrophoneEnabled(on); setMic(on); } catch { /* nekad */ }
  };
  const toggleCam = async () => {
    const on = !cam;
    try {
      const pub = await roomRef.current?.localParticipant.setCameraEnabled(on);
      setCam(on);
      if (on && pub?.track && selfVideoEl.current) pub.track.attach(selfVideoEl.current);
    } catch { /* nekad */ }
  };

  const round = (bg: string): React.CSSProperties => ({
    width: 56, height: 56, borderRadius: '50%', border: 'none', background: bg, color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div role="dialog" aria-label={session.title} data-testid="interview-call"
      style={{ position: 'fixed', inset: 0, background: '#1c211c', zIndex: 1000, display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div style={{ padding: '12px 16px', color: '#f5b06c', fontWeight: 800, fontSize: 15, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.title}</span>
        <span style={{ color: connected ? '#98d886' : '#c3c9b3', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{failed ? 'Fel' : connected ? 'Ansluten' : 'Ansluter …'}</span>
      </div>
      <div style={{ flex: 1, position: 'relative', background: '#000', margin: '0 12px', borderRadius: 12, overflow: 'hidden' }}>
        <video ref={remoteVideo} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: remoteCam ? 'block' : 'none' }} />
        {!remoteCam && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c3c9b3', fontSize: 15, padding: 24, textAlign: 'center' }}>
            {failed || (remoteName ? `${remoteName} – kameran är av` : 'Väntar på att den andra ansluter …')}
          </div>
        )}
        {cam && (
          <div style={{ position: 'absolute', bottom: 16, right: 16, width: 112, height: 150, borderRadius: 10, overflow: 'hidden', border: '2px solid #e5882a', background: '#000' }}>
            <video ref={selfVideo} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
          </div>
        )}
      </div>
      <div style={{ padding: 16, display: 'flex', justifyContent: 'center', gap: 16 }}>
        <button aria-label={mic ? 'Stäng av mikrofonen' : 'Slå på mikrofonen'} onClick={toggleMic} style={round(mic ? '#4E594C' : '#b3261e')}>{mic ? <Mic size={22} /> : <MicOff size={22} />}</button>
        <button aria-label={cam ? 'Stäng av kameran' : 'Slå på kameran'} onClick={toggleCam} style={round(cam ? '#4E594C' : '#b3261e')}>{cam ? <Video size={22} /> : <VideoOff size={22} />}</button>
        <button aria-label="Lämna intervjun" onClick={onClose} style={round('#b3261e')}><PhoneOff size={22} /></button>
      </div>
    </div>
  );
}
